/**
 * The sign-in's device half — DEV-022 (Hamza's list, 17 Oct 2026, item 2) and the Apple/Google
 * return (item 24).
 *
 * The email that carries the code now also carries a one-tap Sign in link, and Apple/Google send
 * the person back with an authorization code in the URL. Both are safe because this browser (or
 * the installed app) keeps a secret the service only ever sees hashed: a sign-in started here
 * completes only when the secret is presented, so the link works on this device and nowhere else.
 *
 * Kept in local storage (not session storage) on purpose: the email's link opens in a new tab.
 * What is kept is a random secret and the pending sign-in's handles — never a code, a document,
 * a key or a session.
 */

const DEVICE_KEY = 'signbento.sign_in.device'
const PENDING_KEY = 'signbento.sign_in.pending'
/** Tells the tab that asked for the code that another tab finished with the email's link. */
export const SIGN_IN_CHANNEL = 'signbento.sign_in'

export type PendingMethod = 'email_otp' | 'apple' | 'google'

/** A sign-in this device started and has not finished. */
export interface PendingSignIn {
  readonly challenge_id: string
  readonly method: PendingMethod
  /** Where the code went (email), when there is one. */
  readonly email: string | null
  /** Exactly what the start sent as `continuation`, echoed on completion. */
  readonly continuation: string | null
  readonly expires_at: string
  readonly sent_at: string
  /** Set when a Google/Apple sign-in needs the account's email code first (the link step). */
  readonly link_provider?: 'apple' | 'google'
}

function storage(): Storage | null {
  try {
    return globalThis.localStorage ?? null
  } catch {
    return null
  }
}

function base64Url(bytes: Uint8Array): string {
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '')
}

function hex(bytes: Uint8Array): string {
  return [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('')
}

/**
 * This device's secret, made once. Without storage there is none, and a sign-in started here
 * falls back to typing the code (the service accepts an unbound email code, never an unbound
 * Apple/Google return).
 */
export function deviceSecret(create = true): string | null {
  const store = storage()
  if (store === null) return null
  try {
    const existing = store.getItem(DEVICE_KEY)
    if (existing !== null && existing.length >= 32) return existing
    if (!create) return null
    const secret = base64Url(crypto.getRandomValues(new Uint8Array(32)))
    store.setItem(DEVICE_KEY, secret)
    return secret
  } catch {
    return null
  }
}

/**
 * After a sign-in finishes: completing it sent the secret to the service, so the next sign-in on
 * this device gets a new one. A secret is presented at most once.
 */
export function forgetDeviceSecret(): void {
  try {
    storage()?.removeItem(DEVICE_KEY)
  } catch {
    // Nothing kept, nothing to forget.
  }
}

/** SHA-256 (hex) of the secret: what the start sends. The secret itself goes only on completion. */
export async function bindingHash(secret: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(secret))
  return hex(new Uint8Array(digest))
}

export function savePendingSignIn(pending: PendingSignIn): void {
  try {
    storage()?.setItem(PENDING_KEY, JSON.stringify(pending))
  } catch {
    // The code can still be typed in this tab; only the link from another tab is lost.
  }
}

/** The pending sign-in, if it is still live; an expired or malformed record is dropped. */
export function readPendingSignIn(now: Date = new Date()): PendingSignIn | null {
  try {
    const raw = storage()?.getItem(PENDING_KEY) ?? null
    if (raw === null) return null
    const parsed = JSON.parse(raw) as Partial<PendingSignIn>
    if (
      typeof parsed.challenge_id !== 'string' ||
      typeof parsed.expires_at !== 'string' ||
      typeof parsed.sent_at !== 'string' ||
      (parsed.method !== 'email_otp' && parsed.method !== 'apple' && parsed.method !== 'google')
    ) {
      clearPendingSignIn()
      return null
    }
    if (Date.parse(parsed.expires_at) <= now.getTime()) {
      clearPendingSignIn()
      return null
    }
    return {
      challenge_id: parsed.challenge_id,
      method: parsed.method,
      email: typeof parsed.email === 'string' ? parsed.email : null,
      continuation: typeof parsed.continuation === 'string' ? parsed.continuation : null,
      expires_at: parsed.expires_at,
      sent_at: parsed.sent_at,
      ...(parsed.link_provider === 'apple' || parsed.link_provider === 'google'
        ? { link_provider: parsed.link_provider }
        : {}),
    }
  } catch {
    return null
  }
}

/**
 * Whether a link or provider return is for the sign-in this device is waiting on. Only then does
 * the page finish it on its own: a crafted link for someone else's challenge (one started without
 * a device binding, which the service still accepts from older clients) must never sign this
 * browser into that other account.
 */
export function startedOnThisDevice(pending: PendingSignIn | null, challengeId: string): boolean {
  return pending !== null && pending.challenge_id === challengeId
}

export function clearPendingSignIn(): void {
  try {
    storage()?.removeItem(PENDING_KEY)
  } catch {
    // Nothing to clear.
  }
}

/** What arrived in the URL fragment: the email's link, or an Apple/Google return. */
export type SignInReturn =
  | {
      readonly kind: 'link'
      readonly challengeId: string
      readonly code: string
      /** The installed app asked for this code: offer to open it there. */
      readonly app: boolean
    }
  | { readonly kind: 'oauth'; readonly state: string; readonly code: string }
  | {
      readonly kind: 'oauth_error'
      readonly state: string | null
      readonly reason: 'cancelled' | 'failed' | 'invalid'
    }

/** Reads the sign-in fragment; anything else in the fragment is not ours and is ignored. */
export function parseSignInReturn(hash: string): SignInReturn | null {
  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash)
  const challengeId = params.get('signin_challenge')
  const code = params.get('signin_code')
  if (challengeId !== null && code !== null && /^\d{4,12}$/.test(code)) {
    return { kind: 'link', challengeId, code, app: params.get('signin_app') === '1' }
  }
  const state = params.get('oauth_state')
  const error = params.get('oauth_error')
  if (error !== null) {
    const reason = error === 'cancelled' || error === 'invalid' ? error : 'failed'
    return { kind: 'oauth_error', state, reason }
  }
  const oauthCode = params.get('oauth_code')
  if (state !== null && oauthCode !== null) return { kind: 'oauth', state, code: oauthCode }
  return null
}

/** The same fragment, for the installed app's URL scheme (`apps/native` registers it). */
export const APP_URL_SCHEME = 'com.instadyn.signbento'

export function appSignInUrl(challengeId: string, code: string): string {
  const fragment = new URLSearchParams({ signin_challenge: challengeId, signin_code: code })
  return `${APP_URL_SCHEME}://sign-in#${fragment.toString()}`
}
